import React, { useState, useEffect, useRef } from 'react';
import { Calendar } from 'lucide-react';

const DateInputBR = ({ 
  value, 
  onChange, 
  placeholder = "DD/MM/AAAA", 
  className = "",
  min,
  max,
  ...props 
}) => {
  const [displayValue, setDisplayValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const hiddenDateInputRef = useRef(null);

  // Converte formato ISO (YYYY-MM-DD) para brasileiro (DD/MM/YYYY)
  const isoToBR = (isoDate) => {
    if (!isoDate || isoDate.length !== 10) return '';
    const [year, month, day] = isoDate.split('-');
    return `${day}/${month}/${year}`;
  };

  // Converte formato brasileiro (DD/MM/YYYY) para ISO (YYYY-MM-DD)
  const brToISO = (brDate) => {
    if (!brDate) return '';
    
    // Remove caracteres não numéricos
    const numbers = brDate.replace(/\D/g, '');
    
    if (numbers.length === 8) {
      const day = numbers.substring(0, 2);
      const month = numbers.substring(2, 4);
      const year = numbers.substring(4, 8);
      
      // Valida se a data é válida
      const date = new Date(year, month - 1, day);
      if (date.getFullYear() == year && 
          date.getMonth() == month - 1 && 
          date.getDate() == day) {
        return `${year}-${month}-${day}`;
      }
    }
    return '';
  };

  // Formatar entrada enquanto digita
  const formatInput = (input) => {
    const numbers = input.replace(/\D/g, '');
    
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 4) {
      return `${numbers.substring(0, 2)}/${numbers.substring(2)}`;
    } else if (numbers.length <= 8) {
      return `${numbers.substring(0, 2)}/${numbers.substring(2, 4)}/${numbers.substring(4)}`;
    }
    
    return `${numbers.substring(0, 2)}/${numbers.substring(2, 4)}/${numbers.substring(4, 8)}`;
  };

  // Atualizar display quando value muda
  useEffect(() => {
    setDisplayValue(isoToBR(value));
  }, [value]);

  const handleInputChange = (e) => {
    const input = e.target.value;
    const formatted = formatInput(input);
    setDisplayValue(formatted);

    // Se a data está completa, converte e envia
    if (formatted.length === 10) {
      const isoDate = brToISO(formatted);
      if (isoDate) {
        onChange({ target: { value: isoDate } });
      }
    } else if (input === '') {
      onChange({ target: { value: '' } });
    }
  };

  const handleKeyDown = (e) => {
    // Permitir apenas números, barra, backspace, delete, tab, enter, setas
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
    const isNumber = (e.key >= '0' && e.key <= '9');
    const isSlash = e.key === '/';
    
    if (!allowedKeys.includes(e.key) && !isNumber && !isSlash) {
      e.preventDefault();
    }
  };

  const handleCalendarClick = () => {
    if (hiddenDateInputRef.current) {
      hiddenDateInputRef.current.showPicker();
    }
  };

  const handleDateChange = (e) => {
    onChange(e);
  };

  return (
    <>
      {/* Input visível para digitação */}
      <div className="relative">
        <input
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          className={`${className} pr-10`}
          maxLength={10}
          {...props}
        />
        <button
          type="button"
          onClick={handleCalendarClick}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <Calendar className="w-4 h-4" />
        </button>
      </div>
      
      {/* Input de data oculto para calendário nativo */}
      <input
        ref={hiddenDateInputRef}
        type="date"
        value={value || ''}
        onChange={handleDateChange}
        min={min}
        max={max}
        className="fixed -z-10 opacity-0 pointer-events-none top-0 left-0 w-0 h-0 overflow-hidden"
        tabIndex={-1}
      />
    </>
  );
};

export default DateInputBR;